// tinker: chordTones — a fixed melody (config.phrase) on a mini piano roll
// over ONE sustained chord (config.chordDegree). A "show the chord's notes"
// toggle splits the melody into chord tones (they melt into the chord) and
// color notes (they sparkle against it) — chord tone = the note's pitch class
// is in chordMidis(chordDegree). Tap any melody note to hear it over the
// chord; ▶ replays the whole phrase. Always ready to continue. A "begin" tap
// resumes audio and starts the chord humming; it stops on unmount.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PIANO_RANGE,
  isInScale,
  chordMidis,
  phraseLength,
} from "../../../lib/homeKeyTheory";
import {
  PATCHES,
  playPhrase,
  playNote,
  startNote,
  resumeAudio,
} from "../../../lib/homeKeyAudio";

const ROWS = PIANO_RANGE.high - PIANO_RANGE.low + 1;
const ROW_H = 100 / ROWS; // viewBox is 100 units tall
const NOTE_H = ROW_H * 0.8;
const yFor = (midi) => (PIANO_RANGE.high - midi) * ROW_H + ROW_H * 0.1;

export default function TinkerChordTones({ moment, onReadyChange }) {
  const config = moment.config || {};
  const phrase = config.phrase || { notes: [] };
  const chordDegree = config.chordDegree || 1;

  const [begun, setBegun] = useState(false);
  const [showChord, setShowChord] = useState(false);
  const [playing, setPlaying] = useState(false);
  const chordRef = useRef(null); // held { stop } handles for the chord
  const playRef = useRef(null);
  const timerRef = useRef(null);

  const chordPcs = useMemo(
    () => new Set(chordMidis(chordDegree).map((m) => m % 12)),
    [chordDegree]
  );
  const isChordTone = (midi) => chordPcs.has(midi % 12);

  useEffect(() => {
    if (onReadyChange) onReadyChange(true);
  }, [onReadyChange]);

  const stopAll = useCallback(() => {
    if (playRef.current && playRef.current.stop) playRef.current.stop();
    playRef.current = null;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (chordRef.current) chordRef.current.forEach((h) => h.stop());
    chordRef.current = null;
    setPlaying(false);
  }, []);
  useEffect(() => () => stopAll(), [stopAll]);

  const begin = () => {
    resumeAudio();
    if (!chordRef.current) {
      chordRef.current = chordMidis(chordDegree).map((m) =>
        startNote(PATCHES.dreamPad, m, 0.55)
      );
    }
    setBegun(true);
  };

  const replay = () => {
    resumeAudio();
    if (playRef.current && playRef.current.stop) playRef.current.stop();
    if (timerRef.current) clearTimeout(timerRef.current);
    setPlaying(true);
    const handle = playPhrase(phrase, PATCHES.softKeys);
    playRef.current = handle && handle.stop ? handle : null;
    const durMs = (handle && handle.durMs) || phraseLength(phrase) || 2000;
    timerRef.current = setTimeout(() => setPlaying(false), durMs + 300);
  };

  const tapNote = (note) => {
    resumeAudio();
    playNote(PATCHES.softKeys, note.midi, Math.max(note.dur || 0, 450));
  };

  const totalMs = Math.max(phraseLength(phrase) || 0, 1);
  const sx = 100 / totalMs;

  return (
    <div className="hk-moment-body">
      <p>
        {config.prompt ||
          "One chord hums underneath while a little melody walks over it. Tap the melody's notes — some melt into the chord, some sparkle against it."}
      </p>

      {!begun && (
        <button type="button" className="hk-btn hk-btn--primary" onClick={begin}>
          begin — let the chord hum
        </button>
      )}

      {begun && (
        <div className="hk-choice-row">
          <button
            type="button"
            className={`hk-choice hk-choice--chip${showChord ? " hk-choice--picked" : ""}`}
            aria-pressed={showChord}
            onClick={() => setShowChord((v) => !v)}
          >
            show the chord's notes
          </button>
          <button
            type="button"
            className={`hk-demo-btn${playing ? " hk-demo-btn--on" : ""}`}
            onClick={replay}
          >
            ▶ play the melody
          </button>
        </div>
      )}

      <div className="hk-roll">
        <svg
          viewBox="0 0 100 100"
          width="100%"
          height="220"
          preserveAspectRatio="none"
          aria-label="mini piano roll: a melody over a sustained chord"
        >
          {/* faint lanes for the scale tones; chord-tone lanes warm up when revealed */}
          {Array.from({ length: ROWS }, (_, i) => {
            const midi = PIANO_RANGE.high - i;
            if (!isInScale(midi)) return null;
            const chordLane = showChord && isChordTone(midi);
            return (
              <rect
                key={midi}
                className={`hk-roll-lane${chordLane ? " hk-roll-lane--chord" : ""}`}
                x="0"
                y={(PIANO_RANGE.high - midi) * ROW_H}
                width="100"
                height={ROW_H}
                fill={
                  chordLane
                    ? "rgba(232, 168, 124, 0.14)"
                    : "rgba(157, 148, 196, 0.07)"
                }
              />
            );
          })}
          {phrase.notes.map((note, i) => {
            const chordTone = isChordTone(note.midi);
            return (
              <rect
                key={i}
                className={`hk-roll-note hk-roll-note--tap${
                  showChord
                    ? chordTone
                      ? " hk-roll-note--chord"
                      : " hk-roll-note--color"
                    : ""
                }`}
                x={note.at * sx}
                y={yFor(note.midi)}
                width={Math.max(note.dur * sx, 1)}
                height={NOTE_H}
                rx="0.8"
                role="button"
                tabIndex={begun ? 0 : -1}
                aria-label={
                  showChord
                    ? chordTone
                      ? "melody note — a chord tone"
                      : "melody note — a color note"
                    : "melody note"
                }
                onClick={() => tapNote(note)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    tapNote(note);
                  }
                }}
              />
            );
          })}
        </svg>
      </div>

      {showChord && (
        <p className="hk-roll-caption" aria-live="polite">
          warm notes live inside the chord — they melt. Lavender ones rub
          against it — they sparkle. A melody needs both.
        </p>
      )}
    </div>
  );
}
